import { Locale } from '../types/global';
import { MediaAsset } from './gallery';

const API_BASE_URL = import.meta.env.VITE_PAYLOAD_API_URL ?? 'http://localhost:3000';
const AUTHORS_ENDPOINT = `${API_BASE_URL}/api/authors`;

const APP_LOCALES: Locale[] = ['en', 'zh'];
const APP_TO_PAYLOAD_LOCALE: Record<Locale, string> = {
  en: 'en',
  zh: 'zh-CN'
};

type PayloadLexicalNode = {
  type: string;
  text?: string;
  children?: PayloadLexicalNode[];
};

type PayloadRichText = {
  root?: PayloadLexicalNode;
};

type PayloadMedia = {
  id: number;
  url: string;
  alt?: string | null;
};

type PayloadAuthorLocale = {
  id: number;
  name?: string | null;
  bio?: PayloadRichText | null;
  avatar?: PayloadMedia | number | string | null;
};

type AuthorListResponseLocale = {
  docs: PayloadAuthorLocale[];
  nextPage?: number | null;
  hasNextPage?: boolean;
};

export type Author = {
  id: string;
  name: Record<Locale, string>;
  bio: Record<Locale, string>;
  avatar: MediaAsset | null;
};

const buildMediaUrl = (path: string): string => {
  try {
    return new URL(path, API_BASE_URL).toString();
  } catch (error) {
    return path;
  }
};

const flattenLexicalNode = (node?: PayloadLexicalNode): string => {
  if (!node) {
    return '';
  }

  if (typeof node.text === 'string') {
    return node.text;
  }

  if (!node.children) {
    return '';
  }

  return node.children.map((child) => flattenLexicalNode(child)).join(' ').trim();
};

const richTextToPlainText = (value?: PayloadRichText | null): string => {
  if (!value?.root) {
    return '';
  }

  return flattenLexicalNode(value.root);
};

const resolvePayloadMedia = (value: PayloadAuthorLocale['avatar']): PayloadMedia | null => {
  if (!value) {
    return null;
  }

  if (typeof value === 'object' && 'url' in value) {
    return value as PayloadMedia;
  }

  return null;
};

const mergeAvatar = (
  current: MediaAsset | null,
  incoming: PayloadAuthorLocale['avatar'],
  locale: Locale
): MediaAsset | null => {
  const payloadMedia = resolvePayloadMedia(incoming);

  if (!payloadMedia) {
    return current;
  }

  const next: MediaAsset = current
    ? {
        ...current,
        id: payloadMedia.id,
        url: buildMediaUrl(payloadMedia.url),
        alt: {
          ...current.alt
        }
      }
    : {
        id: payloadMedia.id,
        url: buildMediaUrl(payloadMedia.url),
        alt: {
          en: '',
          zh: ''
        }
      };

  const altText = typeof payloadMedia.alt === 'string' ? payloadMedia.alt : '';

  next.alt[locale] = altText || next.alt[locale];

  if (locale === 'en' && !next.alt.zh) {
    next.alt.zh = altText;
  }

  return next;
};

type LocaleAuthorsResult = {
  docs: PayloadAuthorLocale[];
  nextPage: number | null;
  hasNextPage: boolean;
};

const fetchAuthorsForLocale = async (
  locale: Locale,
  page: number,
  limit: number
): Promise<LocaleAuthorsResult> => {
  const payloadLocale = APP_TO_PAYLOAD_LOCALE[locale];
  const params = new URLSearchParams({
    limit: String(limit),
    page: String(page),
    locale: payloadLocale,
    depth: '1'
  });

  const response = await fetch(`${AUTHORS_ENDPOINT}?${params.toString()}`);

  if (!response.ok) {
    throw new Error(`Failed to load authors (${locale}): ${response.status} ${response.statusText}`);
  }

  const data = (await response.json()) as AuthorListResponseLocale;

  return {
    docs: data.docs,
    nextPage: data.nextPage ?? null,
    hasNextPage: Boolean(data.hasNextPage ?? (data.nextPage ?? null) !== null)
  };
};

const loadAllAuthorsForLocale = async (locale: Locale): Promise<PayloadAuthorLocale[]> => {
  const limit = 50;
  let page = 1;
  const collected: PayloadAuthorLocale[] = [];

  while (true) {
    const { docs, nextPage } = await fetchAuthorsForLocale(locale, page, limit);
    collected.push(...docs);

    if (!nextPage) {
      break;
    }

    page = nextPage;
  }

  return collected;
};

const mergeLocales = (localeData: Record<Locale, PayloadAuthorLocale[]>): Author[] => {
  const englishDocs = localeData.en;
  const byId = new Map<number, Author>();

  englishDocs.forEach((doc) => {
    byId.set(doc.id, {
      id: String(doc.id),
      name: {
        en: doc.name ?? '',
        zh: ''
      },
      bio: {
        en: richTextToPlainText(doc.bio),
        zh: ''
      },
      avatar: mergeAvatar(null, doc.avatar ?? null, 'en')
    });
  });

  APP_LOCALES.filter((locale) => locale !== 'en').forEach((locale) => {
    const docs = localeData[locale];
    docs.forEach((doc) => {
      const author = byId.get(doc.id);
      if (!author) {
        return;
      }

      author.name[locale] = doc.name ?? author.name.en;
      const localizedBio = richTextToPlainText(doc.bio);
      author.bio[locale] = localizedBio || author.bio.en;
      author.avatar = mergeAvatar(author.avatar, doc.avatar ?? null, locale);
    });
  });

  return Array.from(byId.values()).map((author) => {
    if (author.avatar) {
      author.avatar = {
        ...author.avatar,
        alt: {
          en: author.avatar.alt.en,
          zh: author.avatar.alt.zh || author.avatar.alt.en
        }
      };
    }

    return author;
  });
};

export const loadAuthors = async (): Promise<Author[]> => {
  const englishDocs = await loadAllAuthorsForLocale('en');
  const localeData: Record<Locale, PayloadAuthorLocale[]> = {
    en: englishDocs,
    zh: []
  };

  const otherLocales = await Promise.all(
    APP_LOCALES.filter((locale) => locale !== 'en').map(async (locale) => ({
      locale,
      docs: await loadAllAuthorsForLocale(locale)
    }))
  );

  otherLocales.forEach(({ locale, docs }) => {
    localeData[locale] = docs;
  });

  return mergeLocales(localeData);
};
